// @oasis-permission-inherits: /jerboa-client-error (jerboa:view)
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

import {
  InfiniteQueryPagination,
  useInfiniteQueryPagination,
} from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { Switch } from "~/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ErrorsPerBucketChart } from "~/modules/jerboa-client-error/metrics-chart";
import {
  fetchJerboaClientErrorAnalysisQuery,
  fetchJerboaClientErrorsQuery,
} from "~/modules/jerboa-client-error/queries";
import {
  errorInfoCodeSchema,
  errorInfoSubsystemSchema,
  errorSeveritySchema,
  type ErrorSeverity,
} from "~/modules/jerboa-client-error/schemas";
import { SeverityBadge } from "~/modules/jerboa-client-error/severity-badge";
import { formatTimestamp } from "~/modules/jerboa-client-preview/formatters";

const searchSchema = z.object({
  page: z.number().optional(),
  severity: errorSeveritySchema.optional(),
  code: errorInfoCodeSchema.optional(),
  subsystem: errorInfoSubsystemSchema.optional(),
  sessionId: z.string().optional(),
  jobId: z.string().optional(),
  projectId: z.string().optional(),
  includeNoise: z.boolean().optional(),
});

export const Route = createFileRoute("/_dash/jerboa-client-error/")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

// `severity: "info"` is dropped at emission time by
// `reportJerboaClientError`, so no documents in this collection
// carry that value. Omitting the Info button keeps the filter UI
// honest — filtering on it would always return zero results.
const SEVERITY_FILTERS: Array<{ label: string; value: ErrorSeverity | undefined }> = [
  { label: "All", value: undefined },
  { label: "Fatal", value: "fatal" },
  { label: "Error", value: "error" },
  { label: "Warning", value: "warning" },
];

// Radix Select forbids an empty-string item value, so the "All" entry
// uses a sentinel that maps back to `undefined` (clear the filter).
const ALL_OPTION = "__all__";
const CODE_OPTIONS = errorInfoCodeSchema.options;
const SUBSYSTEM_OPTIONS = errorInfoSubsystemSchema.options;

function RouteComponent() {
  const { page, severity, code, subsystem, sessionId, jobId, projectId, includeNoise } =
    Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const params = useMemo(
    () => ({
      severity,
      code,
      subsystem,
      sessionId,
      jobId,
      projectId,
      // Only send the flag when enabled so the default query stays filtered.
      includeNoise: includeNoise ? true : undefined,
    }),
    [severity, code, subsystem, sessionId, jobId, projectId, includeNoise]
  );

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error } =
    fetchJerboaClientErrorsQuery.useInfiniteQuery({ params });

  const { data: analysisData } = fetchJerboaClientErrorAnalysisQuery.useQuery({
    params: { days: 7 },
  });

  const {
    currentPageIndex,
    currentPageData,
    totalPages,
    onPreviousPage,
    onNextPage,
    onPageChange,
  } = useInfiniteQueryPagination({
    page,
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    navigateToPage: (nextPage) => navigate({ search: { ...params, page: nextPage } }),
  });

  const updateFilters = (next: Partial<typeof params>) => {
    navigate({ search: { ...params, ...next, page: 1 } });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Client Errors</h1>
        {totalPages > 0 && (
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        )}
      </div>

      {analysisData && analysisData.analysis.buckets.length > 0 && (
        <div className="rounded-md border bg-muted/20 p-4">
          <div className="mb-3">
            <p className="text-sm font-medium">Errors per 2 hours (last 7 days)</p>
            <p className="text-xs text-muted-foreground">
              Stacked by severity — fatal / error / warning per 2-hour bucket
            </p>
          </div>
          <ErrorsPerBucketChart buckets={analysisData.analysis.buckets} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {SEVERITY_FILTERS.map((filter) => (
            <Button
              key={filter.label}
              type="button"
              size="sm"
              variant={severity === filter.value ? "default" : "outline"}
              onClick={() => updateFilters({ severity: filter.value })}
            >
              {filter.label}
            </Button>
          ))}
        </div>

        <Select
          value={code ?? ALL_OPTION}
          onValueChange={(value) =>
            updateFilters({ code: value === ALL_OPTION ? undefined : (value as typeof code) })
          }
        >
          <SelectTrigger className="h-8 w-44 text-xs">
            <SelectValue placeholder="All codes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_OPTION}>All codes</SelectItem>
            {CODE_OPTIONS.map((option) => (
              <SelectItem key={option} value={option} className="font-mono text-xs">
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={subsystem ?? ALL_OPTION}
          onValueChange={(value) =>
            updateFilters({
              subsystem: value === ALL_OPTION ? undefined : (value as typeof subsystem),
            })
          }
        >
          <SelectTrigger className="h-8 w-44 text-xs">
            <SelectValue placeholder="All subsystems" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_OPTION}>All subsystems</SelectItem>
            {SUBSYSTEM_OPTIONS.map((option) => (
              <SelectItem key={option} value={option} className="font-mono text-xs">
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <Switch
            id="include-noise"
            checked={includeNoise === true}
            onCheckedChange={(checked) =>
              updateFilters({ includeNoise: checked ? true : undefined })
            }
          />
          <Label htmlFor="include-noise" className="text-xs text-muted-foreground">
            Include noise
          </Label>
        </div>
      </div>

      {(sessionId != null || jobId != null || projectId != null) && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {sessionId != null && (
            <FilterChip
              label="Session"
              value={sessionId}
              onClear={() => updateFilters({ sessionId: undefined })}
            />
          )}
          {jobId != null && (
            <FilterChip
              label="Job"
              value={jobId}
              onClear={() => updateFilters({ jobId: undefined })}
            />
          )}
          {projectId != null && (
            <FilterChip
              label="Project"
              value={projectId}
              onClear={() => updateFilters({ projectId: undefined })}
            />
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load client errors</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      )}

      {!isLoading && currentPageData && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created At</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Subsystem</TableHead>
              <TableHead>Context</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Browser</TableHead>
              <TableHead>Mirage</TableHead>
              <TableHead>Jerboa</TableHead>
              {includeNoise && <TableHead>Noise</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentPageData.page.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() =>
                  router.navigate({
                    to: "/jerboa-client-error/$id",
                    params: { id: row.id },
                  })
                }
              >
                <TableCell>{formatTimestamp(row.createdAt)}</TableCell>
                <TableCell>
                  <SeverityBadge severity={row.errorInfo.severity} />
                </TableCell>
                <TableCell className="font-mono text-xs">{row.errorInfo.code}</TableCell>
                <TableCell className="font-mono text-xs">{row.errorInfo.subsystem}</TableCell>
                <TableCell className="font-mono text-xs">{row.errorInfo.context}</TableCell>
                <TableCell className="max-w-[20rem] truncate text-xs" title={row.errorInfo.message}>
                  {row.errorInfo.message}
                </TableCell>
                <TableCell className="text-xs">{row.browserInfo?.browserName ?? "-"}</TableCell>
                <TableCell className="font-mono text-xs">
                  <CommitLink repo="mirage-web" sha={row.appVersion} />
                </TableCell>
                <TableCell className="font-mono text-xs">
                  <CommitLink repo="jerboa" sha={row.jerboaVersion} />
                </TableCell>
                {includeNoise && (
                  <TableCell className="text-xs">
                    {row.noise ? (
                      <Badge
                        variant="outline"
                        className="font-mono text-[10px]"
                        title={row.noiseReason ?? undefined}
                      >
                        {row.noiseReason ?? "noise"}
                      </Badge>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {currentPageData.page.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={includeNoise ? 10 : 9}
                  className="text-center text-muted-foreground"
                >
                  No client errors found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// Short commit-SHA link. The list shows both build versions side by
// side: `app_version` = mirage-web (studio) commit, `jerboa_version` =
// jerboa (renderer) commit. Each links to its own repo.
function CommitLink({
  repo,
  sha,
}: {
  repo: "mirage-web" | "jerboa";
  sha: string | null | undefined;
}) {
  if (!sha) {
    return <>-</>;
  }
  return (
    <a
      href={`https://github.com/bungeeapp/${repo}/commit/${sha}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline"
      title={`${repo} ${sha}`}
      onClick={(event) => event.stopPropagation()}
    >
      {sha.slice(0, 7)}
    </a>
  );
}

function FilterChip({
  label,
  value,
  onClear,
}: {
  label: string;
  value: string;
  onClear: () => void;
}) {
  return (
    <button
      type="button"
      className="rounded-md border bg-muted/40 px-2 py-1 font-mono hover:bg-muted"
      onClick={onClear}
      title={`Clear ${label} filter`}
    >
      <span className="text-muted-foreground">{label}:</span> {value}
      <span className="ml-2 text-muted-foreground">×</span>
    </button>
  );
}
